import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    position: "",
    role: "employee",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (!token || !userData) {
      navigate("/login/admin");
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== "admin") {
      navigate("/login/admin");
      return;
    }

    fetchEmployees(token);
  }, [navigate]);

  const fetchEmployees = async (token) => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = () => {
    setEditMode(false);
    setCurrentEmployee(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      position: "",
      role: "employee",
    });
    setShowDialog(true);
  };

  const handleEditEmployee = (employee) => {
    setEditMode(true);
    setCurrentEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      password: "",
      position: employee.position,
      role: employee.role,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      if (editMode) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`${API}/employees/${currentEmployee.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/employees`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Employee added successfully");
      }

      setShowDialog(false);
      fetchEmployees(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Employee deleted successfully");
      fetchEmployees(token);
    } catch (error) {
      toast.error("Failed to delete employee");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-employees-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Manage Employees</h1>
            <p className="text-slate-400">Add, edit, or delete employee data</p>
          </div>
          <Button
            onClick={handleAddEmployee}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-employee-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-slate-800">
                <TableHead className="text-slate-300">Name</TableHead>
                <TableHead className="text-slate-300">Email</TableHead>
                <TableHead className="text-slate-300">Position</TableHead>
                <TableHead className="text-slate-300">Role</TableHead>
                <TableHead className="text-slate-300 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                    <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No employee data yet</p>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className="border-slate-700 hover:bg-slate-750"
                    data-testid={`employee-row-${employee.id}`}
                  >
                    <TableCell className="font-medium text-white">
                      {employee.name}
                    </TableCell>
                    <TableCell className="text-slate-300">{employee.email}</TableCell>
                    <TableCell className="text-slate-300">{employee.position}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          employee.role === "admin"
                            ? "bg-purple-900 text-purple-200"
                            : "bg-blue-900 text-blue-200"
                        }`}
                      >
                        {employee.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditEmployee(employee)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-950/50"
                          data-testid={`edit-employee-${employee.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEmployee(employee.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-950/50"
                          data-testid={`delete-employee-${employee.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-white" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editMode ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200">
                  Full Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="employee-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="employee-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">
                  Password {editMode && "(leave blank if not changing)"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editMode}
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="employee-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="position" className="text-slate-200">
                  Position
                </Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="employee-position-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-200">
                  Role
                </Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                  data-testid="employee-role-select"
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                data-testid="cancel-employee-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-employee-button"
              >
                {editMode ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}